import {
  GetDeviceResponse,
  ListResponse as DevicesListResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import {
  DeviceType_Enum,
  SipProfile,
  ListResponse as SipProfilesListResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/sip-profiles/v1/sip_profiles.pb';
import type { RenameLineChangeSet } from '@weave/schema-gen-ts/dist/schemas/phone-devices/lines/line_api.pb';
import type { QueryClient } from 'react-query';
import { DevicesQueryKey } from '@frontend/api-devices';
import { i18next } from '@frontend/i18n';
import { queryKeys as phoneQueryKeys } from '../../../query-keys';

type ChangedField = {
  field: 'extensionNumber' | 'deviceName' | 'internalCallerName' | 'voicemailBoxName';
  displayName: string;
};

/**
 * Inspects a RenameLineChangeSet and returns an array of fields that actually changed
 * (where `before !== after`).
 *
 * Example output:
 * [
 *   { field: 'extensionNumber', displayName: 'Extension' },
 *   { field: 'deviceName', displayName: 'Device Name' }
 * ]
 */
export const getChangedFields = (changes: RenameLineChangeSet | undefined): ChangedField[] => {
  if (!changes) return [];

  const fieldMap: { field: ChangedField['field']; displayName: string; change: typeof changes.extensionNumber }[] = [
    { field: 'extensionNumber', displayName: 'Extension', change: changes.extensionNumber },
    { field: 'deviceName', displayName: 'Device Name', change: changes.deviceName },
    { field: 'internalCallerName', displayName: 'Internal Caller ID Name', change: changes.internalCallerName },
    { field: 'voicemailBoxName', displayName: 'Voicemail Box Name', change: changes.voicemailBoxName },
  ];

  return fieldMap
    .filter(({ change }) => change && change.before !== change.after)
    .map(({ field, displayName }) => ({ field, displayName }));
};

/**
 * Builds a human-readable success message listing the changed fields.
 *
 * Examples:
 * - "Extension successfully saved."
 * - "Extension and Device Name successfully saved."
 * - "Extension, Device Name, and Internal Caller ID Name successfully saved."
 */
export const buildRenameLineSuccessMessage = (changedFields: ChangedField[]): string => {
  if (changedFields.length === 0) return i18next.t('Successfully saved.', { ns: 'phone', keyPrefix: 'devices' });

  const names = changedFields.map((f) => f.displayName);

  let fieldList: string;
  if (names.length === 1) {
    fieldList = names[0];
  } else if (names.length === 2) {
    fieldList = `${names[0]} and ${names[1]}`;
  } else {
    fieldList = `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }

  return i18next.t('{{fieldList}} successfully saved.', { fieldList, ns: 'phone', keyPrefix: 'devices' });
};

interface UpdateDeviceCachesParams {
  queryClient: QueryClient;
  changes: RenameLineChangeSet | undefined;
  deviceId: string;
  tenantId: string | undefined;
  sipProfileId: string | undefined;
}

/**
 * Updates all three relevant query caches after a RenameLine mutation:
 * 1. DevicesList cache (table listing)
 * 2. DeviceById cache (single device detail page)
 * 3. SipProfile List cache (sip profiles for a device)
 */
export const updateDeviceCaches = ({
  queryClient,
  changes,
  deviceId,
  tenantId,
  sipProfileId,
}: UpdateDeviceCachesParams) => {
  if (!changes) return;

  const newDeviceName = changes.deviceName?.after;
  const newExtensionNumber = changes.extensionNumber?.after;
  const newCallerName = changes.internalCallerName?.after;

  const deviceNameChanged = changes.deviceName && changes.deviceName.before !== newDeviceName;
  const extensionChanged = changes.extensionNumber && changes.extensionNumber.before !== newExtensionNumber;
  const callerNameChanged = changes.internalCallerName && changes.internalCallerName.before !== newCallerName;

  // 1. Update DevicesList cache (used by DeviceSettingsTable)
  const devicesListKey = [tenantId, ...DevicesQueryKey.queryKeys.devicesList()];
  // Only update if the cache already exists — calling setQueryData when there is no existing entry
  // causes React Query v3 to create a cache entry with undefined data (status: 'success'), which
  // prevents a proper fetch and leads to schema validation errors.
  if (queryClient.getQueryData(devicesListKey)) {
    queryClient.setQueryData(devicesListKey, (oldData: DevicesListResponse | undefined) => {
      if (!oldData?.devices) return oldData as DevicesListResponse;
      return {
        ...oldData,
        devices: oldData.devices.map((d) => {
          if (d.deviceId !== deviceId) return d;

          let updated = { ...d };

          if (deviceNameChanged) {
            updated = { ...updated, name: newDeviceName ?? '' };
          }

          if (extensionChanged || deviceNameChanged) {
            updated = {
              ...updated,
              sipProfiles: updated.sipProfiles?.map((sp) => {
                if (sp.sipProfileId !== sipProfileId) return sp;
                let updatedSp = { ...sp };
                if (extensionChanged) {
                  updatedSp = {
                    ...updatedSp,
                    extension: {
                      ...updatedSp.extension,
                      extensionId: updatedSp.extension?.extensionId ?? '',
                      extensionNumber: Number(newExtensionNumber),
                    },
                  };
                }
                if (deviceNameChanged) {
                  updatedSp = { ...updatedSp, name: newDeviceName ?? '' };
                }
                return updatedSp;
              }),
            };
          }

          return updated;
        }),
      };
    });
  }

  // 2. Update DeviceById cache (used by DeviceSettings detail page)
  const deviceByIdKey = [deviceId, ...DevicesQueryKey.queryKeys.deviceById()];
  if (queryClient.getQueryData(deviceByIdKey)) {
    queryClient.setQueryData(deviceByIdKey, (oldData: GetDeviceResponse | undefined) => {
      if (!oldData?.device) return oldData as GetDeviceResponse;
      if (oldData.device.deviceId !== deviceId) return oldData;

      let updatedDevice = { ...oldData.device };

      if (deviceNameChanged) {
        updatedDevice = { ...updatedDevice, name: newDeviceName ?? '' };
      }

      return { ...oldData, device: updatedDevice };
    });
  }

  // 3. Invalidate VoicemailBox caches when device name changes (voicemail box name may have changed too)
  if (deviceNameChanged) {
    // Invalidate the voicemail box list used by VoicemailBoxIndex
    queryClient.invalidateQueries([tenantId, ...phoneQueryKeys.settings.listVoicemailBox()]);
    // Invalidate all individual voicemail box detail queries used by VoicemailDetail.
    queryClient.invalidateQueries([tenantId, phoneQueryKeys.settings.base, 'voicemail-box']);
  }

  // 4. Update SipProfile List cache (used by DeviceSettings detail page)
  const sipProfileKey = [tenantId, ...phoneQueryKeys.settings.listDeviceSipProfiles(deviceId)];
  // Same guard — skip if not yet cached to avoid poisoning the cache with undefined.
  if (queryClient.getQueryData(sipProfileKey)) {
    queryClient.setQueryData(sipProfileKey, (oldData: SipProfilesListResponse | undefined) => {
      if (!oldData?.sipProfiles) return oldData as SipProfilesListResponse;
      return {
        ...oldData,
        sipProfiles: oldData.sipProfiles.map((sp: SipProfile) => {
          // Only update sip profiles belonging to this device
          if (sp.sipProfileId !== sipProfileId) return sp;

          let updatedSp = { ...sp };

          if (callerNameChanged) {
            updatedSp = {
              ...updatedSp,
              internalCallerName: newCallerName ?? '',
              name: newDeviceName ?? '',
            };
          }

          if (extensionChanged && newExtensionNumber) {
            updatedSp = {
              ...updatedSp,
              extension: {
                ...updatedSp.extension,
                extensionId: updatedSp.extension?.extensionId ?? '',
                extensionNumber: Number(newExtensionNumber),
              },
            };
          }

          if (deviceNameChanged && newDeviceName) {
            updatedSp = {
              ...updatedSp,
              name: newDeviceName,
              device: {
                ...updatedSp.device,
                deviceId: updatedSp.device?.deviceId ?? '',
                deviceName: newDeviceName,
                deviceType: updatedSp.device?.deviceType ?? DeviceType_Enum.UNSPECIFIED,
              },
            };
          }

          return updatedSp;
        }),
      };
    });
  }
};
